import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/spinner';
import { apiService } from '../../api/axiosClient';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import PointToolbar from './PointToolbar';


const TablePointUse = props => {
  const [loading, setLoading] = useState(true)
  const {reload, setReload} = props;
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('po_id')
  const [selected, setSelected] = useState([])

  const loadPoints = 
    async (sort, username, column) => {
      try {
        const response = await apiService.get('/points', {
          params: {
            username,
            kind: "",
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }
    
  useEffect(() => {
    loadPoints(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadPoints(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('po_datetime')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadPoints(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      flex: 0.1,
      minWidth: 50,
      field: 'po_id',
      headerName: '포인트 ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.po_id}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 50,
      field: 'username',
      headerName: '사용자 ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.username}
        </Typography>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'po_orderid',
      headerName: '주문번호',
      renderCell: params => (
        <Link to={`/admin/order/${params.row.po_orderid}`}>
          {params.row.po_orderid}
        </Link>
      )
    },
    {
      flex: 0.1,
      minWidth: 50,
      field: 'po_point',
      headerName: '포인트',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.po_point}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 50,
      field: 'po_source',
      headerName: '포인트유형',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.po_source}
        </Typography>
      )
    },
    {
      flex: 0.1,
      minWidth: 50,
      field: 'po_use_point',
      headerName: '사용 포인트',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.po_use_point}
        </Typography>
      )
    },
    {
      flex: 0.15,
      minWidth: 50,
      field: 'po_expire_date',
      headerName: '사용기한',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.po_expire_date).format("YYYY-MM-DD")}
        </Typography>
      )
    },
    {
      flex: 0.15,
      field: 'po_datetime',
      minWidth: 140,
      headerName: '등록날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.po_datetime).format("YYYY-MM-DD")}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='포인트 내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.po_id}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: PointToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.po_id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TablePointUse
